import React, { useState } from 'react';
import { Search, Car, AlertTriangle, Calendar, Shield, Hash, CheckCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Badge } from '../components/shared/Badge';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Card } from '../components/shared/Card';
import { Table } from '../components/shared/Table';
import { FraudAnalysisBreakdown } from '../components/FraudAnalysisBreakdown';
import { CarrierSwitchTimeline } from '../components/CarrierSwitchTimeline';
import { CarrierRelationshipGraph } from '../components/CarrierRelationshipGraph';
import { FraudSummaryCards } from '../components/FraudSummaryCards';
import { Navbar } from '../components/shared/Navbar';
import { vinApi, PlateMatch } from '../services/api';
import { exportFraudReport } from '../utils/fraud-report-export';

interface VINLookupData {
  vin: string;
  vehicleInfo: {
    make: string | null;
    model: string | null;
    year: number | null;
    type: string | null;
    bodyClass: string | null;
    licensePlates: string[];
  };
  nhtsaData: Record<string, unknown> | null;
  summary: {
    totalCarriers: number;
    totalCrashes: number;
    totalInspections: number;
    totalViolations: number;
    totalFatalities: number;
    totalInjuries: number;
    oosEvents: number;
    oosViolations: number;
    firstSeen: string | null;
    lastSeen: string | null;
  };
  crashes: Array<{
    crashId?: string;
    reportNumber?: string;
    reportDate: string;
    reportState?: string;
    carrierName: string;
    fatalities: number;
    injuries: number;
    location: string;
  }>;
  inspections: Array<{
    inspectionId?: string;
    inspectionDate: string;
    reportState?: string;
    carrierName: string;
    inspectionLevel: string;
    totalViolations: number;
    oosTotal: number;
    location: string;
  }>;
  carriers: Array<{
    dotNumber: number;
    legalName: string;
    crashCount: number;
    inspectionCount: number;
    firstSeen: string;
    lastSeen: string;
  }>;
  shellGameScore: {
    score: number;
    level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    indicators: string[];
  } | null;
}

interface TimelineData {
  vin: string;
  totalEvents: number;
  uniqueCarriers: number;
  switchCount: number;
  suspiciousSwitches: number;
  switches: Array<{
    fromDotNumber: number;
    fromCarrierName: string;
    lastSeenAt: string;
    toDotNumber: number;
    toCarrierName: string;
    firstSeenAt: string;
    daysBetween: number;
    suspicious: boolean;
    suspicionReasons: string[];
  }>;
  carrierPeriods: Array<{
    dotNumber: number;
    carrierName: string;
    startDate: string;
    endDate: string;
    eventCount: number;
  }>;
  timeline: Array<Record<string, unknown>>;
  advancedFraudAnalysis?: {
    level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    reason: string;
    details: string[];
    fraudDetected: boolean;
    phase1RedFlags: {
      badEventQuickSwitchClean: boolean;
      rapidBackAndForth: boolean;
      ghostCarrierViolation: boolean;
      dumpAndReturnAfterIdle: boolean;
      shellCarrierOnlyBadEvents: boolean;
    };
    phase2Indicators: {
      relatedCarriers: boolean;
      sameOwner: boolean;
      sameAddress: boolean;
      samePhone: boolean;
      recentlyCreatedCarriers: boolean;
    };
    phase3Whitelist: {
      longGaps: boolean;
      legitimateSale: boolean;
      linearProgression: boolean;
      rentalUnit: boolean;
      postRevocationDispersal: boolean;
    };
    decisionMatrix: {
      condition: string;
      action: string;
    };
    carrierStatuses?: Array<{
      dotNumber: number;
      carrierName: string;
      status: string;
      statusCode: string;
    }>;
  };
  isRentalUnit?: boolean;
  rentalCompanies?: Array<{ dotNumber: string; companyName: string }>;
}

const VINLookup: React.FC = () => {
  const [vinInput, setVinInput] = useState('');
  const [plateInput, setPlateInput] = useState('');
  const [plateState, setPlateState] = useState('');
  const [plateLoading, setPlateLoading] = useState(false);
  const [plateMatches, setPlateMatches] = useState<PlateMatch[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<VINLookupData | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Core VIN lookup — shared by the VIN form and by a license-plate match.
  const lookupVin = async (rawVin: string) => {
    const vin = rawVin.trim().toUpperCase();
    setVinInput(vin);
    setLoading(true);
    setError('');
    setData(null);
    setTimelineData(null);
    setShowDetailedAnalysis(false);
    setPlateMatches(null);

    try {
      const response = await fetch(`/api/vins/${vin}/lookup`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to lookup VIN');
      }

      const result = await response.json();
      setData(result.data || result);

      // Also fetch timeline data for advanced fraud analysis
      try {
        const timeline = await vinApi.getVINTimeline(vin) as unknown as TimelineData;
        setTimelineData(timeline);
        // Auto-expand if high or critical risk
        if ((timeline?.advancedFraudAnalysis?.level as string) === 'HIGH' ||
            (timeline?.advancedFraudAnalysis?.level as string) === 'CRITICAL') {
          setShowDetailedAnalysis(true);
        }
      } catch (timelineErr) {
        console.error('Failed to fetch timeline data:', timelineErr);
        // Continue without timeline data
      }
    } catch (err: unknown) {
      console.error('VIN lookup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to lookup VIN. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const vin = vinInput.trim().toUpperCase();
    if (!vin) {
      setError('Please enter a VIN');
      return;
    }
    if (vin.length !== 17) {
      setError('VIN must be exactly 17 characters');
      return;
    }
    await lookupVin(vin);
  };

  // Find equipment by license plate, then drive the VIN lookup.
  const handlePlateSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const plate = plateInput.trim();
    if (!plate) {
      setError('Please enter a license plate');
      return;
    }
    setPlateLoading(true);
    setError('');
    setData(null);
    setTimelineData(null);
    setPlateMatches(null);
    try {
      const result = await vinApi.searchByPlate(plate, plateState.trim() || undefined);
      if (result.matches.length === 0) {
        setError(
          `No equipment found for plate "${plate.toUpperCase()}"` +
            (plateState.trim() ? ` in ${plateState.trim().toUpperCase()}` : '') + '.'
        );
      } else if (result.matches.length === 1) {
        await lookupVin(result.matches[0].vin);
      } else {
        setPlateMatches(result.matches);
      }
    } catch (err: unknown) {
      console.error('Plate search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to search by plate. Please try again.');
    } finally {
      setPlateLoading(false);
    }
  };

  // Fetch detailed analysis on demand
  const fetchDetailedAnalysis = async () => {
    if (timelineData || !data) return;

    setLoadingTimeline(true);
    try {
      const timeline = await vinApi.getVINTimeline(data.vin) as unknown as TimelineData;
      setTimelineData(timeline);
      setShowDetailedAnalysis(true);
    } catch (err) {
      console.error('Failed to fetch detailed analysis:', err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  // Export handler
  const handleExportFraudReport = () => {
    if (timelineData) {
      exportFraudReport(data?.vin || '', timelineData);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Car className="w-8 h-8 text-blue-600" />
            VIN Intelligence Lookup
          </h1>
          <p className="mt-2 text-gray-600">
            Enter any VIN to get complete vehicle history including crashes, inspections, violations, carrier tracking, and advanced fraud analysis
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="vin-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Identification Number (VIN)
                </label>
                <input
                  id="vin-input"
                  type="text"
                  value={vinInput}
                  onChange={(e) => setVinInput(e.target.value)}
                  placeholder="Enter 17-character VIN (e.g., 1XKADB9X4JJ504992)"
                  maxLength={17}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg uppercase"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {vinInput.length}/17 characters
                </p>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading || vinInput.length !== 17}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
                >
                  <Search className="w-5 h-5" />
                  Search
                </button>
              </div>
            </div>
          </form>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3 text-xs font-medium text-gray-400">
            <div className="flex-1 border-t border-gray-200" />
            OR SEARCH BY LICENSE PLATE
            <div className="flex-1 border-t border-gray-200" />
          </div>

          <form onSubmit={handlePlateSearch}>
            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="plate-input" className="block text-sm font-medium text-gray-700 mb-2">
                  License Plate
                </label>
                <input
                  id="plate-input"
                  type="text"
                  value={plateInput}
                  onChange={(e) => setPlateInput(e.target.value)}
                  placeholder="e.g., ZP21097"
                  maxLength={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg uppercase"
                />
              </div>
              <div className="w-28">
                <label htmlFor="plate-state" className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-gray-400">(opt)</span>
                </label>
                <input
                  id="plate-state"
                  type="text"
                  value={plateState}
                  onChange={(e) => setPlateState(e.target.value)}
                  placeholder="CA"
                  maxLength={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-lg uppercase"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={plateLoading || !plateInput.trim()}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
                >
                  <Search className="w-5 h-5" />
                  Find
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Looks up the VIN from roadside-inspection records for this plate.
            </p>
          </form>
        </Card>

        {/* Plate matches — a plate can map to more than one VIN over time */}
        {plateMatches && plateMatches.length > 0 && (
          <Card className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {plateMatches.length} vehicles found for this plate
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Plates get reused across units — select a VIN to view its full history.
            </p>
            <div className="space-y-2">
              {plateMatches.map((m) => (
                <button
                  key={m.vin}
                  onClick={() => lookupVin(m.vin)}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-gray-900">{m.vin}</span>
                    <span className="text-sm text-gray-500">
                      {(m.make || '—')} · {m.inspectionCount} insp · {m.states.join(', ')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {m.carriers.map((c) => c.legalName || `DOT ${c.dotNumber}`).join('; ')}
                    {m.lastSeen ? ` · last seen ${String(m.lastSeen).slice(0, 10)}` : ''}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Loading State */}
        {(loading || plateLoading) && (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner message="Looking up VIN..." size="lg" />
            <p className="mt-4 text-gray-600">Searching 9.3M VIN records...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">Error</span>
            </div>
            <p className="mt-2 text-red-700">{error}</p>
          </div>
        )}

        {/* Results */}
        {data && (
          <div className="space-y-6">
            {/* Vehicle Information */}
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Car className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Vehicle Information</h2>
                </div>
                <div className="font-mono text-lg text-gray-700 bg-gray-100 px-4 py-2 rounded">
                  {data.vin}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <span className="text-sm text-gray-600">Make</span>
                  <p className="text-lg font-semibold text-gray-900">{data.vehicleInfo.make || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Model</span>
                  <p className="text-lg font-semibold text-gray-900">{data.vehicleInfo.model || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Year</span>
                  <p className="text-lg font-semibold text-gray-900">{data.vehicleInfo.year || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Type</span>
                  <p className="text-lg font-semibold text-gray-900">{data.vehicleInfo.type || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Body Class</span>
                  <p className="text-lg font-semibold text-gray-900">{data.vehicleInfo.bodyClass || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">License Plates</span>
                  <p className="text-lg font-semibold text-gray-900">
                    {data.vehicleInfo.licensePlates.length > 0 ? data.vehicleInfo.licensePlates.join(', ') : 'None recorded'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="text-center">
                <div className="text-3xl font-bold text-blue-600">{data.summary.totalCarriers}</div>
                <div className="text-sm text-gray-600 mt-1">Carriers</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-red-600">{data.summary.totalCrashes}</div>
                <div className="text-sm text-gray-600 mt-1">Crashes</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-yellow-600">{data.summary.totalInspections}</div>
                <div className="text-sm text-gray-600 mt-1">Inspections</div>
              </Card>
              <Card className="text-center">
                <div className="text-3xl font-bold text-purple-600">{data.summary.totalViolations}</div>
                <div className="text-sm text-gray-600 mt-1">Violations</div>
              </Card>
            </div>

            {/* Advanced Fraud Analysis Summary Cards */}
            {timelineData?.advancedFraudAnalysis && (
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-purple-600" />
                    <h2 className="text-xl font-bold text-gray-900">Advanced Fraud Analysis</h2>
                    <Badge variant="info" size="sm">Phase 1/2/3 Detection</Badge>
                  </div>
                  {timelineData && (
                    <button
                      onClick={handleExportFraudReport}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Export Report
                    </button>
                  )}
                </div>

                <FraudSummaryCards
                  level={timelineData.advancedFraudAnalysis.level}
                  fraudDetected={timelineData.advancedFraudAnalysis.fraudDetected}
                  phase1RedFlags={timelineData.advancedFraudAnalysis.phase1RedFlags}
                  phase2Indicators={timelineData.advancedFraudAnalysis.phase2Indicators}
                  phase3Whitelist={timelineData.advancedFraudAnalysis.phase3Whitelist}
                  reason={timelineData.advancedFraudAnalysis.reason}
                />

                {/* Toggle for detailed view */}
                <button
                  onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                  className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {showDetailedAnalysis ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Hide Detailed Phase Analysis
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      View Detailed Phase Analysis
                    </>
                  )}
                </button>
              </Card>
            )}

            {/* Button to load detailed analysis if not yet loaded */}
            {!timelineData && data.summary.totalCarriers > 1 && (
              <Card className="text-center py-6">
                <Shield className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Fraud Analysis Available</h3>
                <p className="text-gray-600 mb-4">
                  This VIN has appeared with {data.summary.totalCarriers} carriers. Run advanced Phase 1/2/3 fraud detection?
                </p>
                <button
                  onClick={fetchDetailedAnalysis}
                  disabled={loadingTimeline}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-semibold"
                >
                  {loadingTimeline ? 'Analyzing...' : 'Run Detailed Fraud Analysis'}
                </button>
              </Card>
            )}

            {/* Detailed Fraud Analysis Breakdown */}
            {showDetailedAnalysis && timelineData?.advancedFraudAnalysis && (
              <FraudAnalysisBreakdown
                fraudAnalysis={timelineData.advancedFraudAnalysis}
                vin={data.vin}
                onExport={handleExportFraudReport}
              />
            )}

            {/* Carrier Switch Timeline */}
            {timelineData && timelineData.switches && timelineData.switches.length > 0 && (
              <CarrierSwitchTimeline
                switches={timelineData.switches}
                carrierPeriods={timelineData.carrierPeriods}
                timeline={timelineData.timeline as any}
              />
            )}

            {/* Carrier Relationship Graph */}
            {timelineData?.advancedFraudAnalysis && timelineData.carrierPeriods && timelineData.carrierPeriods.length > 1 && (
              <CarrierRelationshipGraph
                carriers={timelineData.carrierPeriods.map(p => ({
                  dotNumber: p.dotNumber,
                  carrierName: p.carrierName,
                  status: timelineData.advancedFraudAnalysis?.carrierStatuses?.find(cs => cs.dotNumber === p.dotNumber)?.status,
                  statusCode: timelineData.advancedFraudAnalysis?.carrierStatuses?.find(cs => cs.dotNumber === p.dotNumber)?.statusCode,
                }))}
                phase2Indicators={timelineData.advancedFraudAnalysis.phase2Indicators}
              />
            )}

            {/* Shell Game Detection (Legacy) */}
            {data.shellGameScore && !timelineData?.advancedFraudAnalysis && (
              <Card className={`border-2 ${
                data.shellGameScore.level === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                data.shellGameScore.level === 'HIGH' ? 'border-orange-500 bg-orange-50' :
                data.shellGameScore.level === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
                'border-green-500 bg-green-50'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-red-600" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Shell Game Risk Assessment</h2>
                      <p className="text-sm text-gray-600 mt-1">Analyzing vehicle movement patterns across carriers</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-red-600">{data.shellGameScore.score}</div>
                    <Badge variant={
                      data.shellGameScore.level === 'CRITICAL' ? 'danger' :
                      data.shellGameScore.level === 'HIGH' ? 'danger' :
                      data.shellGameScore.level === 'MEDIUM' ? 'warning' : 'success'
                    }>
                      {data.shellGameScore.level} RISK
                    </Badge>
                  </div>
                </div>

                {data.shellGameScore.indicators.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <p className="text-sm font-semibold text-gray-700">Risk Indicators:</p>
                    <ul className="space-y-2">
                      {data.shellGameScore.indicators.map((indicator, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{indicator}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}

            {/* Carrier History */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Hash className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">Carrier History</h2>
                <Badge variant="info" size="sm">{data.carriers.length} carriers</Badge>
              </div>

              {data.carriers.length > 0 ? (
                <div className="space-y-3">
                  {data.carriers.map((carrier, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{carrier.legalName}</p>
                          <p className="text-sm text-gray-600">DOT {carrier.dotNumber}</p>
                        </div>
                        <div className="text-right text-sm">
                          <div className="flex gap-2">
                            <Badge variant="warning" size="sm">{carrier.crashCount} crashes</Badge>
                            <Badge variant="info" size="sm">{carrier.inspectionCount} inspections</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-gray-600">First Seen:</span>
                          <p className="font-semibold text-gray-900">
                            {new Date(carrier.firstSeen).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Seen:</span>
                          <p className="font-semibold text-gray-900">
                            {new Date(carrier.lastSeen).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No carrier history found</p>
              )}
            </Card>

            {/* Crash History */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <h2 className="text-xl font-bold text-gray-900">Crash History</h2>
                <Badge variant="danger" size="sm">{data.crashes.length} crashes</Badge>
              </div>

              {data.crashes.length > 0 ? (
                <Table
                  columns={[
                    { key: 'reportDate', header: 'Date', render: (row) => new Date(row.reportDate).toLocaleDateString() },
                    { key: 'reportState', header: 'State' },
                    { key: 'carrierName', header: 'Carrier' },
                    { key: 'fatalities', header: 'Fatalities', render: (row) => row.fatalities > 0 ? <span className="text-red-600 font-bold">{row.fatalities}</span> : '0' },
                    { key: 'injuries', header: 'Injuries', render: (row) => row.injuries > 0 ? <span className="text-yellow-600 font-bold">{row.injuries}</span> : '0' },
                    { key: 'location', header: 'Location' },
                  ]}
                  data={data.crashes}
                  keyExtractor={(crash) => crash.crashId || crash.reportNumber || `crash-${crash.reportDate}`}
                />
              ) : (
                <p className="text-gray-500 text-center py-4">No crash history found</p>
              )}
            </Card>

            {/* Inspection History */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Inspection History</h2>
                <Badge variant="info" size="sm">{data.inspections.length} inspections</Badge>
              </div>

              {data.inspections.length > 0 ? (
                <Table
                  columns={[
                    { key: 'inspectionDate', header: 'Date', render: (row) => new Date(row.inspectionDate).toLocaleDateString() },
                    { key: 'reportState', header: 'State' },
                    { key: 'carrierName', header: 'Carrier' },
                    { key: 'inspectionLevel', header: 'Level' },
                    { key: 'totalViolations', header: 'Violations', render: (row) => row.totalViolations > 0 ? <Badge variant="warning" size="sm">{row.totalViolations}</Badge> : '0' },
                    { key: 'oosTotal', header: 'OOS', render: (row) => row.oosTotal > 0 ? <Badge variant="danger" size="sm">{row.oosTotal}</Badge> : '0' },
                    { key: 'location', header: 'Location' },
                  ]}
                  data={data.inspections}
                  keyExtractor={(inspection) => inspection.inspectionId || `inspection-${inspection.inspectionDate}`}
                />
              ) : (
                <p className="text-gray-500 text-center py-4">No inspection history found</p>
              )}
            </Card>

            {/* Timeline Summary */}
            {data.summary.firstSeen && data.summary.lastSeen && (
              <Card>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-green-600" />
                  <h2 className="text-xl font-bold text-gray-900">Activity Timeline</h2>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <span className="text-sm text-gray-600">First Seen in Database</span>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(data.summary.firstSeen).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Last Seen in Database</span>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(data.summary.lastSeen).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default VINLookup;
